import type { SaveOutcome } from './save-queue';
import type { Geometry } from './types';

/**
 * 几何写入的**版本权威**（M3B 独立复审第三轮，BLOCKER 1 / BLOCKER 2 的结构性修复）。
 *
 * 前两轮暴露出来的两个症状其实是同一个病根：**版本权威被夹带在数据里传递，
 * 而不是在真正发出写入的那一刻从单一权威来源解析**。
 *
 * - BLOCKER 1（NEW-1 残留）：`applyGeometry` 收 `Partial<Geometry>`，而
 *   `Geometry` 含 `version`——任何把完整 `Geometry` 快照传进来的调用点
 *   （第二轮修了画布，属性面板漏了）都会让"编辑那一刻"的旧 version 悄悄
 *   盖掉最新版本，undo 因此带着过期版本号被服务端拒绝，UI 已经视觉回滚、
 *   数据库却停在编辑后的值——两边永久分叉。TypeScript 挡不住这个：完整
 *   `Geometry` 在结构类型系统里**就是**合法的 `Partial<Geometry>`，
 *   `Pick<>` 也只是编译期的视图、运行时字段一个不少。
 * - BLOCKER 2（NEW-6）：最新已知版本活在 React state（`local` 叠加在 query
 *   缓存上），通过只在渲染时刷新的 ref 读取；保存成功的结果要等 `setLocal`
 *   → 下一次渲染才变成"已知"。两次渲染之间发起的任何操作读到的都是旧版本，
 *   快速连续编辑因此会跟**自己**发生版本冲突。
 *
 * 修复设计（一个机制同时消除两个症状）：
 *
 * 1. **命令历史只存几何值**（`GeometryPatch`——x/y/w/h/locked，没有
 *    version）。`applyGeometryPatch` 用**逐字段显式读取**合成下一个几何，
 *    不做 `...patch` 展开——就算运行时真的塞进来一个带 version 的完整
 *    `Geometry`（绕过类型系统），version 也**结构上**到不了写入路径。
 * 2. **`VersionLedger` 是唯一的版本权威**：一个同步的、跟渲染周期完全无关
 *    的 Map。它从每一次服务端响应**同步**学习（成功结果的新 version、
 *    版本冲突里服务端附带的 `current.version` 都是权威知识），只增不减
 *    （服务端版本号单调递增，max 合并永远安全——账本里的值只可能来自
 *    服务端，绝不会"超前"于真实版本）。
 * 3. **version 在发送时刻才附着**（见 `save-queue.ts` 的 `resolveVersion`）：
 *    排队的操作不带 version，PATCH 真正发出的那一刻才从账本取最新值——
 *    "保存成功必须在下一个操作**发出**之前推进版本知识"因此按字面成立，
 *    不依赖 React 渲染。
 *
 * 冲突解决的"用我的覆盖"也不再需要特殊通道：冲突结果落地时账本已经学到
 * 服务端的当前版本，重新提交几何值即可第一次点击就成功（M-2 语义保持）。
 * 刻意**不做**的两件事：不预测 in-flight 请求会产生的下一个版本号（猜错会
 * 制造更诡异的冲突，等于变相削弱乐观版本检查），也不把 flush 串行排在
 * in-flight 响应后面（卸载路径 H-1 必须立即发出请求，不能等一个文档销毁后
 * 可能永远不会 settle 的 promise）。残余暴露：RTT 大于 debounce 间隔的持续
 * 快速编辑仍可能撞上一次**可恢复**的版本冲突（走既有黄框 UI），但不存在
 * 静默分叉。
 */

/** 命令历史/属性面板/画布允许携带的全部几何字段——注意没有 `version`。 */
export type GeometryPatch = Partial<Pick<Geometry, 'x' | 'y' | 'w' | 'h' | 'locked'>>;

/**
 * 每个对象最新已知的服务端版本。同步更新（在响应处理器里，不在渲染里），
 * max 合并保证单调：账本永远记录"这个客户端见过的最新服务端版本"。
 */
export class VersionLedger {
  private readonly versions = new Map<string, number>();

  /** 见到一个服务端出具的版本号（场景数据、保存结果、冲突负载）就记下来。 */
  note(locationId: string, version: number): void {
    const prev = this.versions.get(locationId);
    if (prev === undefined || version > prev) this.versions.set(locationId, version);
  }

  get(locationId: string): number {
    return this.versions.get(locationId) ?? 0;
  }

  /**
   * 从一批保存结果同步学习：
   * - 成功：服务端已把对象推进到 `r.version`。
   * - 版本冲突：服务端在拒绝里附带了权威的 `current`——它的 `version` 同样
   *   是刚出炉的权威知识（这正是"用我的覆盖"第一次点击就能成功的原因）。
   * - 其它硬拒绝（场景不匹配/已归档……）：不携带版本信息，不学。
   * 整批传输失败（`ok: false`）同理没有可学的东西。
   */
  noteOutcome(outcome: SaveOutcome): void {
    if (!outcome.ok) return;
    for (const r of outcome.results) {
      if (r.ok) {
        this.note(r.locationId, r.version);
      } else if (r.code === 'COMMON.VERSION_CONFLICT' && r.current) {
        const current = r.current as { version?: unknown };
        if (typeof current.version === 'number') this.note(r.locationId, current.version);
      }
    }
  }

  clear(): void {
    this.versions.clear();
  }
}

/**
 * 用一个几何补丁合成下一个几何。**逐字段显式读取**，绝不 `...patch`：
 * `version` 只能来自第三个参数（也就是 `VersionLedger`），patch 里就算在
 * 运行时混进了 version/rotation/z/color 等字段也影响不到任何东西。
 */
export function applyGeometryPatch(
  current: Geometry,
  patch: GeometryPatch,
  version: number,
): Geometry {
  return {
    ...current,
    x: patch.x ?? current.x,
    y: patch.y ?? current.y,
    w: patch.w ?? current.w,
    h: patch.h ?? current.h,
    locked: patch.locked ?? current.locked,
    version,
  };
}
